import React, {FunctionComponent, useEffect, useState} from "react";
import {Box, Typography, useTheme} from "@mui/material";
import { useAppSelector} from "../../../../../../Config/Hooks";
import {addMinutes, differenceInMinutes, endOfDay, format, parseISO} from "date-fns";
import {isArray} from "lodash";
import {truncateNumber} from "../../../../../../Utils/NumberUtil";
import { AChartX, Skeleton} from "@atiautomacao/ati-ui-library";
import DataNotFound from "../../../../../../Shared/Components/DataNotFoundMessage";
import {FindStringCurrentByEquipmentId} from "../../InverterReactQuery";
import {LocalDateTimeFormatISO} from "../../../../../../Utils/DateFormatPatternUtils";


interface StringCurrent{
    teleObjectId: number,
    name: string,
    dateTime: Date,
    value: number,
}

const LineChartStringCurrent: FunctionComponent<{equipmentId: number, date:Date}> = ({equipmentId, date}) => {
    const theme = useTheme();
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const [startDate, setStartDate] = useState<string>();
    const [endDate, setEndDate] = useState<string>();
    const { data, isLoading } = FindStringCurrentByEquipmentId(equipmentId, startDate, endDate);
    const [series, setSeries] = useState<Array<any>>([]);
    const [dataPU, setDataPU] = useState<Array<StringCurrent>>([]);
    const [maxYAxis, setMaxYAxis] = useState<number>(0);
    const [activeParams, setActiveParams] = useState<any[]>([]);

    const getStringMatrix = (stringCurrents: StringCurrent[]) : StringCurrent[][] => {
        if(stringCurrents && stringCurrents.length > 0){
            const grouped = stringCurrents.reduce((acc:any, item:any) => {
                if (!acc[item.name]) {
                    acc[item.name] = [];
                }
                acc[item.name].push(item);
                return acc;
            }, {} as Record<string, any[]>);


            const sortedNames = Object.keys(grouped).sort((a, b) =>
                a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
            );

            const sortedMatrix = sortedNames.map(name => grouped[name]);
            return Object.values(sortedMatrix) as StringCurrent[][]
        }
        return []
    }
    
    const getMaxYAxis = (matrix: StringCurrent[][]) => {
        const maxValueBrute = matrix.reduce((currentMax, item) => {
            const maxPerItem = Math.max(...item.map(item => item.value));
            return Math.max(currentMax, maxPerItem);
        }, -Infinity);
        const maxValueFinal = Math.ceil(maxValueBrute / 5) * 5
        return (Math.max(maxValueFinal || 0, 5))
    }

    useEffect(() => {
        if(data?.data && equipmentId){
            setDataPU(data?.data)
        }else{
            setDataPU([])
        }
    }, [data, equipmentId, startDate, endDate]);

    useEffect(() => {
        setStartDate(LocalDateTimeFormatISO(date))
        setEndDate(LocalDateTimeFormatISO(endOfDay(date)))
    }, [date]);

    const adjustDataPoints = (dataPoints: any[]): any[] => {
        if (dataPoints.length === 0) return dataPoints;

        const firstPoint = dataPoints[0];
        const lastPoint = dataPoints.at(-1);

        const firstDateTime = parseISO(firstPoint.dateTime);
        const lastDateTime = parseISO(lastPoint.dateTime);

        const startTime = new Date(firstDateTime).setHours(4, 0, 0, 0);
        const endTime = new Date(lastDateTime).setHours(20, 0, 0, 0);

        const interval = 5;
        const { teleObjectId, name } = firstPoint;

        const paddingStart: any[] = [];
        let currentStart = new Date(startTime);

        const diffStart = differenceInMinutes(firstDateTime, currentStart);
        if (diffStart > 0) {
            for (let m = 0; m < diffStart; m += interval) {
                paddingStart.push({
                    dateTime: format(addMinutes(currentStart, m), 'yyyy-MM-dd HH:mm:ss.S'),
                    teleObjectId,
                    name,
                    value: null
                });
            }
        }

        const paddingEnd: any[] = [];
        let currentEnd = addMinutes(lastDateTime, interval);
        const limitEnd = new Date(endTime);

        while (currentEnd <= limitEnd) {
            paddingEnd.push({
                dateTime: format(currentEnd, 'yyyy-MM-dd HH:mm:ss.S'),
                teleObjectId,
                name,
                value: null
            });
            currentEnd = addMinutes(currentEnd, interval);
        }

        return [...paddingStart, ...dataPoints, ...paddingEnd];
    };


    const getSeriesData = (matrix: StringCurrent[][]) => {
        const adjustedMatrix = matrix.map((group: StringCurrent[]) => {
            return adjustDataPoints(group)
        })

        return adjustedMatrix.map((group: StringCurrent[]) => {
            return {
                name: group[0].name,
                data: group.map((item) => {
                    const formattedValue = item.value == null
                        ? "-"
                        : truncateNumber(item.value, 2);

                    return [item.dateTime || "", formattedValue];
                }),
                type: 'line',
                unity: 'A',
                showSymbol: false
            };
        });
    }

    useEffect(() => {
        const handleUpdate = (e: any) => setActiveParams(e.detail);
        globalThis.addEventListener('update-tooltip-data', handleUpdate);
        return () => globalThis.removeEventListener('update-tooltip-data', handleUpdate);
    }, []);


    useEffect(() => {
        if(isArray(dataPU) && dataPU && dataPU.length > 0){
            const matrix = getStringMatrix(dataPU)
            setSeries(getSeriesData(matrix))
            setMaxYAxis(getMaxYAxis(matrix))
        }else {
            setSeries([])
        }
    }, [dataPU]);

    // @ts-ignore
    const option: AChartXProps['option'] = {
        color: ['rgb(25, 118, 210)', 'rgb(255,198,10)'],
        tooltip: {
            trigger: 'axis',
            showContent: true,
            formatter: function (params: any) {
                if (params && params.length > 0) {
                    globalThis.dispatchEvent(new CustomEvent('update-tooltip-data', { detail: params }));
                }
                return "";
            }
        },
        toolbox: {
            right: '5%',
            top: -8,
            itemSize: 15,
            feature: {
                dataZoom: {
                    yAxisIndex: 'none'
                },
                magicType: { show: false, type: ['line', 'bar'] },
                restore: { show: true }
            }
        },
        dataZoom: [
            {
                type: 'inside',
                throttle: 50
            },
        ],
        legend: {
            show: true,
            type: 'scroll',
            orient: 'horizontal',
            bottom: -6,
            left: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            textStyle: {
                opacity: 1
            },
            itemStyle: {
                opacity: 1
            },
            data: series.map(el => el.name)
        },
        grid: {
            containLabel: true,
            top: 10,
            bottom: 40,
            left: '4%',
            right: '4%',
        },
        label: {
            precision: 1,
            position: 'right',
            valueAnimation: true,
            fontFamily: 'monospace'
        },
        animationDuration: 2000,
        xAxis: {
            name: "Horário",
            type:'time',
            nameLocation: 'middle',
            minInterval: 3 * 60 * 60 * 1000,
            axisLabel: {
                formatter: '{HH}:{mm}'
            },
            nameGap: 30
        },
        yAxis: [{
            name: 'A',
            min: 0,
            max: maxYAxis,
            nameLocation: 'middle',
            nameGap: 60
        }],
        series: series,
    };

    const [content, setContent] = useState<JSX.Element>()

    useEffect(() => {
        if(isLoading){
            setContent(<Skeleton animation="wave" height={400} variant="rounded" width={"100%"} />)
        }else if(series.length > 0 && dataPU.length > 0){
            setContent(<AChartX
                key={`line-${openSubMenu}`}
                option={option}
                width={'100%'}
                height={400}
                loading={false}
                theme={theme.palette.mode}
            />)
        }else {
            setContent(
                <DataNotFound/>
            )
        }
    }, [series, isLoading, openSubMenu]);

    return (
        <Box
            onMouseLeave={() => setActiveParams([])}
            sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}
        >
            <Box sx={{ height: 400 }}>
                {content}
            </Box>
            {activeParams.length > 0 && (
                <Box sx={{
                    mt: 2,
                    mb: 1,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    p: 2,
                    backgroudColor: '#fff',
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    position: 'relative',

                }}>
                    <Typography sx={{fontSize: '14px', fontWeight: 'bold', pr: 1, borderRight: '1px solid #ddd', mr: 1 }}>
                        {format(new Date(activeParams[0].value[0]), "HH:mm")}
                    </Typography>

                    {activeParams.map((item, idx) => (
                        <Box key={idx.valueOf()} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color, mr: 0.5 }} />
                            <Typography
                                sx={{ fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                                color: '#FFFFF',
                                whiteSpace: 'nowrap',
                                fontSize: '14px',
                                lineHeight: '1'
                            }}>
                                {item.seriesName}: <b style={{ fontSize: '14px', lineHeight: '1.5',color: '#424242' }}>{item.value[1]}A</b>
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default LineChartStringCurrent;